import React, { useEffect, useState } from 'react';
import { Box, Card, Chip, Container, SwipeableDrawer, TextField, Tooltip, IconButton } from '@mui/material';
import { dispatch, useSelector } from '../../../../redux/store';
import { PATH_DASHBOARD } from '../../../../routes/paths';
import useSettings from '../../../../hooks/useSettings';
import Page from '../../../../components/Page';
import HeaderBreadcrumbs from '../../../../components/HeaderBreadcrumbs';
import DataGridTable from '../../../../components/table/DataGridTable';
import { accessVerify, capitalize, safeJSONParse, utcMoment, handleNumber } from '../../../../utils/common';
import { getStockTransfers, notifyBossAboutStockTransferMistake, updateTransferReceived } from '../../../../redux/slices/stockManagementRedux/stockTransferRedux';
import moment from 'moment/moment';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import useSound from 'use-sound';
import successFx from '../../../../assets/sounds/success.wav';
import errorFx from '../../../../assets/sounds/errorSoundForNotAvailableItems.wav';
import { fCurrency } from 'src/utils/formatNumber';
import PdfViewerDrawer from 'src/components/pdfViewerDrawer';
import { PictureAsPdf } from '@material-ui/icons';
import { BASE_URL, COMPANY_DATA_FOR_PDF } from 'src/config';



const ViewTransfer = () => {
  const { themeStretch } = useSettings();
  const [purchaseNotesList, setPurchaseNotesList] = useState([]);
  const [rowSelection, setRowSelection] = useState(false);
  const [isModelOpen, setIsModelOpen] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [selectedData, setSelectedData] = useState([]);
  const [isPdfDrawerOpen, setIsPdfDrawerOpen] = useState(false);
  const [pdfRow, setPdfRow] = useState(null);

  let loggedInUser;
  ({ loggedInUser } = useSelector((state) => state.user));

  const [playErrorSound] = useSound(errorFx, {
    volume: 0.8,
    playbackRate: 1.2,
  });

  const [playSuccessSound] = useSound(successFx, {
    volume: 0.8,
    playbackRate: 1.2,
  });

  const { data: stockTransferData, addData: stockTransferUpdatedData } = useSelector((state) => state?.stockTransfer);
  const { selectedLocation } = useSelector((state) => state?.location);

  useEffect(() => {
    dispatch(getStockTransfers({
      locationId: selectedLocation?.id,
    }));
  }, [selectedLocation]);

  useEffect(() => {
    setPurchaseNotesList(
      stockTransferData?.map((value) => {
        const stockDetailsArray = safeJSONParse(value?.stockDetailsArray);
        return {
          ...value,
          stockDetailsArray: stockDetailsArray?.map((values) => {
            return { ...values, currentQty: values?.stockDetails?.stockCurrentQty };
          }),
        };
      })
    );
  }, [stockTransferData]);

  const columns = purchaseNotesList?.[0]
    ? Object.keys(purchaseNotesList?.[0])
      .map((value) => {
        if (value === 'transferAddedUserId' || value === 'stockDetailsArray') {
          return null;
        } else if (value === 'transferStatus') {
          return {
            accessorKey: value,
            header: capitalize(value),
            Cell: ({ cell, row }) => {
              switch (row?.original?.transferStatus) {
                case '0':
                  return <Chip label={'Pending'} color={'warning'} />;
                case '1':
                  return <Chip label={'Done'} color={'success'} />;
                default:
                  return '-';
              }
            },
          };
        } else if (value === 'transferDateTime') {
          return {
            accessorKey: value,
            header: capitalize(value),
            Cell: ({ cell, row }) => utcMoment(row.original.transferDateTime).format('DD MMMM YYYY'),
          };
        } else if (value === 'confirmedAt') {
          return {
            accessorKey: value,
            header: capitalize(value),
            Cell: ({ cell, row }) => row.original.confirmedAt ? utcMoment(row.original.confirmedAt).format('DD MMMM YYYY, HH:mm:ss') : null,
          };
        } else if (value === 'totalAmount') {
          return {
            accessorKey: value,
            header: capitalize(value),
            Cell: ({ row }) => fCurrency(row?.original?.totalAmount),
          };
        }
        else {
          return {
            accessorKey: value,
            header: capitalize(value),
          };
        }
      })
      .filter(Boolean)
    : [];

  const columnForStockDetails = selectedData?.stockDetailsArray?.[0]
    ? Object.keys(selectedData?.stockDetailsArray?.[0])
      .map((value) => {
        if (value === 'stockDetails') {
          return {
            accessorKey: value,
            header: 'Stock Code',
            Cell: ({ cell, row }) => row?.original?.stockDetails?.stockCode,
          };
        }
        if (value === 'transferStockStatus') {
          return {
            accessorKey: value,
            header: 'Transfer Status',
            Cell: ({ cell, row }) => (row?.original?.transferStockStatus === 0 ? <Chip label={'Pending'} color={'warning'} /> : <Chip label={'Done'} color={'success'} />),
          };
        }
        if (value === 'costPerUnit') {
          return {
            accessorKey: 'costPerUnit',
            header: capitalize(value),
            Cell: ({ row }) => fCurrency(row?.original?.costPerUnit),
          };
        }
        if (value === 'totalAmount') {
          return {
            accessorKey: 'totalAmount',
            header: capitalize(value),
            Cell: ({ row }) => fCurrency(row?.original?.totalAmount),
          };
        }
        if (value === 'fromLocationId') {
          return null;
        }
        return {
          accessorKey: value,
          header: capitalize(value),
        };
      })
      .filter(Boolean)
    : [];

  useEffect(() => {
    if (stockTransferUpdatedData?.data && !showErrorMessage) {
      dispatch(getStockTransfers());
      setIsModelOpen(false);
    }
  }, [stockTransferUpdatedData]);

  const getStatusText = (value) => {
    switch (value) {
      case '0':
        return 'Pending';
      case '1':
        return 'Done';
      default:
        return '-';
    }
  };






  const handleDownload = async (row) => {
    const transferData = {
      ...row?.original,
      pdfType: "STOCK_TRANSFER"
    };
    setPdfRow(transferData);
    setIsPdfDrawerOpen(true);
  };

  return (
    <Page title='View Transferred Stock'>
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs heading='View Transferred Stock' links={[{ name: 'Dashboard', href: PATH_DASHBOARD.root }, { name: 'Stock', href: '' }, { name: 'View Transferred Stock' }]} action={[]} />

        {accessVerify('STOCK_TRANSFER_VIEW') && (
          <DataGridTable
            name={'View Stock'}
            data={purchaseNotesList}
            column={columns}
            isLoading={false}
            rowSelection={rowSelection}
            setRowSelection={setRowSelection}
            keyField={'stockId'}
            enableRowActions={true}
            isRowClickable={true}
            onRowClick={(row) => {
              setIsModelOpen(true);
              setSelectedData({ ...row.original, location: selectedLocation });
            }}
            renderRowActionItems={(row) => {
              return [
                <>
                  <Tooltip title='Download PDF'>
                    <IconButton color="error" onClick={(e) => {
                      e.stopPropagation();
                      handleDownload(row);
                    }}>
                      <PictureAsPdf />
                    </IconButton>
                  </Tooltip>
                </>,
              ];
            }}
          />
        )}

        <SwipeableDrawer
          anchor={'top'}
          open={isModelOpen}
          onOpen={() => setIsModelOpen(true)}
          onClose={() => setIsModelOpen(false)}
          PaperProps={{
            sx: {
              width: '90%',
              height: '90%',
              marginLeft: '5%',
              marginTop: '3%',
              borderRadius: '10px',
            },
          }}
        >
          <Box sx={{ p: 2 }}>
            <h1>Transfer Receiving Management</h1>
            <br />
            <br />
            <Card
              sx={{
                p: 3,
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: '#eee',
                },
              }}
            >
              {showErrorMessage ? (
                <>
                  <LoadingButton
                    onClick={() => {
                      setShowErrorMessage(false);
                    }}
                    color={'error'}
                    variant='contained'
                    size='large'
                    style={{ width: '100%' }}
                  >
                    {showErrorMessage}
                  </LoadingButton>
                </>
              ) : (
                <TextField
                  type='text'
                  label={`Enter Barcode`}
                  sx={{ width: '100%' }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.target.select();
                      let updatedWithLastValue = false;
                      console.log(selectedData.fromLocationId, selectedLocation.id);
                      
                      if (selectedData.fromLocationId && selectedData.fromLocationId == selectedLocation.id) {
                        setShowErrorMessage(`This transfer cannot be received because the source location is the same as the destination.`);
                        return
                      }
                      setSelectedData((selectedDataValue) => {
                        return {
                          ...selectedDataValue,
                          stockDetailsArray: selectedDataValue?.stockDetailsArray?.map((currentItemList) => {
                            if (currentItemList?.stockDetails?.stockCode === event.target.value) {
                              updatedWithLastValue = true;

                              return {
                                ...currentItemList,
                                transferStockStatus: 1,
                              };
                            }
                            return currentItemList;
                          }),
                        };
                      });
                      if (updatedWithLastValue) {
                        playSuccessSound();
                      } else {
                        playErrorSound();
                        dispatch(
                          notifyBossAboutStockTransferMistake({
                            code: event.target.value,
                            locationId: selectedLocation?.id,
                            tag: 'Transfer',
                            message: `[Transfer Mistake]\n\nBarcode: ${event.target.value}\nTran ID: ${selectedData?.transferId}\nLocation: ${selectedLocation?.name}\nScanned By: ${loggedInUser?.name}`,
                          })
                        );
                        setShowErrorMessage(`The last scanned Barcode ( ${event.target.value} ) cannot be added here. Please contact the sender for verification.`);
                      }
                    }
                  }}
                />
              )}
              <br />
              <br />
              <DataGridTable
                name={'View Stock'}
                data={selectedData?.stockDetailsArray}
                column={columnForStockDetails}
                rowSelection={rowSelection}
                setRowSelection={setRowSelection}
                keyField={'stockId'}
                isLoading={false}
                enablePinning={false}
                isRowClickable={false}
                enableRowActions={false}
                enableRowSelection={false}
                renderRowActionItems={(row) => []}
                maxHeight={`34vh`}
              />
            </Card>
            {/* === TOTAL SUMMARY SECTION === */}
            {(
              <>
                <br />

                <Card
                  sx={{
                    p: 3,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: '#eee',
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontSize: '18px',
                      fontWeight: 600,
                      mt: 1,
                    }}
                  >
                    <span>Total:</span>
                    <span>
                      {fCurrency(selectedData?.totalAmount)}
                    </span>
                  </Box>
                </Card>
              </>
            )}

            <br />
            {selectedData?.transferStatus === '0' && (
              <Card
                sx={{
                  p: 3,
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: '#eee',
                  },
                }}
              >
                <LoadingButton
                  onClick={() => {
                    if (selectedData?.stockDetailsArray?.filter((value) => value?.transferStockStatus === 0).length > 0) {
                      toast.warning('All the items needs to be received to confirm this transfer!');
                    } else if (selectedData.fromLocationId && selectedData.fromLocationId == selectedLocation.id) {
                      toast.warning('This transfer cannot be received because the source location is the same as the destination.');
                    } else {
                      dispatch(getStockTransfers());
                      dispatch(updateTransferReceived(selectedData));
                    }
                  }}
                  variant='contained'
                  size='large'
                  style={{ width: '100%' }}
                >
                  Save Changes
                </LoadingButton>
              </Card>
            )}
          </Box>
        </SwipeableDrawer>
        <PdfViewerDrawer
          open={isPdfDrawerOpen}
          onClose={() => setIsPdfDrawerOpen(false)}
          invoice={pdfRow}
        />
      </Container>
    </Page>
  );
};

export default ViewTransfer;
